import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useMutation } from "react-query-legacy";

import { Button, Modal, Menu, Col } from "antd";
import { TransferItem } from "antd/lib/transfer";

import { FacilitiesListAction, FacilitiesListWrapper } from "./styles/FacilityList.styles";

import {
  useEditServiceContext,
  useRateCardContext,
  useRateCardFacilitiesContext,
} from "components/ratecard/contextProviders";
import { BillingEngineService } from "services";

import FacilitySelect from "./FacilitySelect";

interface FacilitiesListProps {
  onSwitchFacility?: (city: string) => void;
}

const FacilitiesList: React.FC<FacilitiesListProps> = ({ onSwitchFacility }) => {
  // Contexts
  const { rateCardId, activeZonesFacilities, activeZone, dispatch, currentService, zoneByFacilityId, hasFinished } =
    useRateCardContext();
  const { allowEditing, setEditDisabled } = useEditServiceContext();
  const { facilities, facilityById } = useRateCardFacilitiesContext();

  // Local state
  const [visibleModal, setVisibleModal] = useState(false);
  const [newActiveFacilities, setNewSelectedFacilities] = useState<string[]>([]);
  const [newDisabledFacilities, setNewDisabledFacilities] = useState<string[]>([]);

  // Mutation
  const [updateRateCardZones, { status: updateZonesStatus }] = useMutation(BillingEngineService.updateRateCardZones, {
    onSuccess: (data) => {
      dispatch({
        type: "UPDATE_RATE_CARD_ZONES",
        zones: data,
      });
    },
  });

  const setActiveZone = useCallback(
    (zone) =>
      dispatch({
        type: "SET_ACTIVE_ZONE",
        zone,
      }),
    [dispatch]
  );

  const handleFacilitySwitch = useCallback(
    (key: string) => {
      if (zoneByFacilityId) {
        setActiveZone(zoneByFacilityId[key]);
      }

      if (onSwitchFacility) {
        onSwitchFacility(key);
      }
    },
    [onSwitchFacility, zoneByFacilityId, setActiveZone]
  );

  // Effect handles active facilities
  useEffect(() => {
    const isNoneFacilityActives = activeZonesFacilities.length === 0 && hasFinished;
    setEditDisabled(isNoneFacilityActives);
    setVisibleModal(isNoneFacilityActives);
    // eslint-disable-next-line
  }, [activeZonesFacilities]);

  useEffect(() => {
    // Hide modal whenever the current service is changed and disable edit till the API returns results
    setEditDisabled(true);
    setVisibleModal(false);
    // eslint-disable-next-line
  }, [currentService]);

  /**
   * Convert facilities to transfer items
   */
  const transferItems: TransferItem[] = useMemo(() => {
    return (facilities || []).map((facility) => ({
      title: facility.name,
      key: facility.id.toString(),
    }));
  }, [facilities]);

  /**
   * Updates active zones for active rate card
   * @param activated activated cities ids
   * @param deactivated deactivated cities ids
   */
  const triggerChange = (activated: string[], deactivated: string[]) => {
    const activeZones = activated.map((facilityId) => {
      return {
        facilityId,
        // @TODO: orest - move it separate Provider that is responsible for service take always first for now (Default)
        serviceRateId: currentService?.rates[0]?.id,
        isActive: true,
      };
    });

    const inactiveZones = deactivated.map((facilityId) => {
      return {
        facilityId,
        serviceRateId: currentService?.rates[0]?.id,
        isActive: false,
      };
    });

    return updateRateCardZones({
      zones: activeZones.concat(inactiveZones),
      rateCardId: rateCardId!,
    });
  };

  const handleOk = () => {
    // Nothing to update just close the modal
    if (newActiveFacilities.length === 0 && newDisabledFacilities.length === 0) {
      setVisibleModal(false);
      return;
    }

    // Trigger updated
    return triggerChange(newActiveFacilities, newDisabledFacilities)
      ?.then(() => {
        setVisibleModal(false);
      })
      .catch(() => {
        setVisibleModal(true);
      });
  };

  /**
   * Render Menu items
   */
  const renderList = () => {
    return (
      <Menu
        mode="horizontal"
        selectedKeys={activeZone ? [activeZone.facilityId] : []}
        selectable={false}
        onClick={(param) => handleFacilitySwitch(param.key as string)}
      >
        {activeZonesFacilities.map((item: string) => (
          <Menu.Item key={item} disabled={allowEditing && item !== activeZone?.facilityId}>
            {facilityById[item]?.name.toUpperCase()}
          </Menu.Item>
        ))}
      </Menu>
    );
  };

  return (
    <>
      <FacilitiesListWrapper>
        <Col span={24}>{renderList()}</Col>
        <FacilitiesListAction>
          <Button disabled={allowEditing} type="link" onClick={() => setVisibleModal(true)}>
            Edit Facilities
          </Button>
        </FacilitiesListAction>
      </FacilitiesListWrapper>
      <Modal
        title="Activate facilities"
        visible={visibleModal}
        onOk={handleOk}
        onCancel={() => setVisibleModal(false)}
        confirmLoading={updateZonesStatus === "loading"}
      >
        <FacilitySelect
          dataSource={transferItems}
          value={activeZonesFacilities}
          onChange={(active, notActive) => {
            setNewDisabledFacilities(notActive);
            setNewSelectedFacilities(active.filter((a) => a));
          }}
        />
      </Modal>
    </>
  );
};

export default FacilitiesList;
